import { API_BASE_URL, ApiError, api } from './client'
import { m } from '@/lib/paraglide/messages'

// FileRecord mirrors entity.File. Private files (is_public=false, e.g. ID-card
// photos) have an empty `url` and are only reachable via the auth-gated download
// endpoint — use fileDownloadUrl() to reach them.
export interface FileRecord {
    id: string
    url: string
    name?: string
    extension?: string
    size?: number
    mime_type?: string
    is_public: boolean
    created_at: string
    updated_at: string
}

// Auth-gated download endpoint. The browser attaches the HttpOnly session cookie
// on top-level navigation, so an <a href> reaches private files without JS.
export function fileDownloadUrl(id: string): string {
    return `${API_BASE_URL}/files/${id}/download`
}

// Uploads a file via multipart/form-data. Sensitive documents must pass
// isPublic=false so they are never served from the public static mount.
// subdir picks the on-disk folder for public files (server allowlist, e.g.
// 'packing' for packing photo evidence); omit for the server default.
//
// Đi qua CÙNG axios instance với mọi call khác (không phải `fetch` trần) để ăn
// theo interceptor refresh 401: hết hạn access token giữa chừng thì request tự
// gia hạn rồi phát lại, thay vì bắt người dùng đăng nhập lại chỉ vì cái ảnh —
// trong khi mọi thao tác khác trên màn hình vẫn chạy ngon. Kèm theo đó là lỗi
// ApiError chuẩn (giữ status + code + X-Request-ID) và telemetry chung.
// Request interceptor tự bỏ qua Content-Type khi body là FormData, nên trình
// duyệt tự đặt boundary.
export async function uploadFile(
    file: File,
    isPublic: boolean,
    subdir?: string,
): Promise<FileRecord> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('is_public', isPublic ? 'true' : 'false')
    if (subdir) {
        formData.append('subdir', subdir)
    }

    const res = await api.post<FileRecord>('/files', formData)
    if (!res.data) {
        // 2xx mà không có bản ghi tệp: không có id để gắn vào biểu mẫu, coi như
        // hỏng thay vì trả về undefined cho caller tự vấp.
        throw new ApiError(
            0,
            'UPLOAD_NO_RECORD',
            'Upload succeeded but no file record was returned',
        )
    }
    return res.data
}

// Chuyển lỗi của uploadFile thành câu tiếng Việt ĐÚNG với nguyên nhân thật.
// Dùng chung cho mọi ô upload (nhật ký chăm sóc, giấy tờ hộ, ảnh vật tư…) để
// không nơi nào lại chế ra một câu đoán mò riêng.
export function uploadFileErrorMessage(err: unknown): string {
    if (!(err instanceof ApiError)) {
        return m.file_upload_error_generic()
    }
    // Ảnh HEIC/HEIF (mặc định của camera iPhone) cũng là 415 nhưng cần câu
    // hướng dẫn riêng — bảo "sai định dạng" thì người dùng không biết chỉnh gì.
    if (err.code === 'UNSUPPORTED_HEIF_IMAGE') {
        return m.file_upload_error_heif()
    }
    switch (err.status) {
        case 0:
            // status 0 = request chưa từng nhận phản hồi (mất mạng, bị huỷ).
            return m.file_upload_error_network()
        case 401:
            return m.file_upload_error_unauthorized()
        case 403:
            return m.file_upload_error_forbidden()
        case 413:
            return m.file_upload_error_too_large()
        case 415:
            return m.file_upload_error_unsupported()
        default:
            return m.file_upload_error_generic()
    }
}
